<template>
  <div :class="['PlanBenefits', `PlanBenefits--${mode}`]">
    <div class="PlanBenefits-header">
      <span class="PlanBenefits-title">{{ t('ui.subscription.details.benefits.title') }}:</span>
    </div>

    <ul class="PlanBenefits-list">
      <li v-for="benefit of benefits" :key="benefit.text" class="PlanBenefits-item">
        <div class="PlanBenefits-iconWrapper">
          <Icon :icon="benefit.icon" size="md" class="PlanBenefits-icon" />
        </div>

        <span class="PlanBenefits-text">{{ benefit.text }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Icon from '../../atoms/Icon/Icon.vue';
import type { PlanBenefitsProps } from './PlanBenefits.model';

withDefaults(defineProps<PlanBenefitsProps>(), {
  mode: 'compact',
});

const { t } = useI18n();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/responsive';
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .PlanBenefits {
    &-list {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: vars.$dimensions-d-3;
      align-self: stretch;
    }

    &-item {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-3;
      align-self: stretch;
    }

    &-text {
      @include text.text('md');
      color: vars.$atoms-benefit-text-color;
    }

    &-iconWrapper {
      display: flex;
    }

    &-icon {
      color: var(--plan-benefits-icon-color, #{vars.$atoms-benefit-icon-color});
    }
  }

  // Variants
  .PlanBenefits--compact {
    .PlanBenefits {
      &-header {
        display: none;
      }
    }
  }

  .PlanBenefits--detailed {
    .PlanBenefits {
      &-header {
        margin-bottom: vars.$dimensions-d-2;
      }

      &-title {
        @include text.text('md', vars.$typography-weight-strong);
        color: vars.$organisms-card-contentcard-subscription-details-default-label-color;
      }

      &-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: vars.$dimensions-d-2;

        @include responsive.mobile {
          display: flex;
          flex-direction: column;
        }
      }

      &-item {
        display: flex;
        align-items: center;
        align-content: center;
      }
      &-iconWrapper {
        padding: vars.$dimensions-d-1;
        background-color: vars.$molecules-breakdown-icon-benefits-bg-color;
        align-items: center;
        gap: 12px;
        border-radius: vars.$dimensions-d-1;
      }

      &-icon {
        color: vars.$molecules-breakdown-icon-benefits-icon-color;
      }

      &-text {
        @include text.text('sm');
        color: vars.$organisms-card-contentcard-subscription-details-default-secondary-text-color;
      }
    }
  }
}
</style>